import { forwardRef, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Close, ErrorStatus, GrabberReorderToken, InformationStatus, WarningStatus } from '@sas/react-svg';
// Import CSS last
import cx from 'classnames';
import { motion, useAnimate, useDragControls, useMotionValue } from 'framer-motion';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useIsRTL } from '../../../locale';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { isEventMarked } from '../../../marked-events';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import { convertCSSSizeToNumber } from '../../../util/dom';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { BusyStateIndicator } from '../../BusyStateIndicator';
import { Button, ButtonVariant } from '../../Button';
import { Heading, HeadingVariant } from '../../Heading';
import { Icon } from '../../Icon';
import { Flow } from '../../Layouts/Flow';
import { Resizer } from '../../Resizable';
import { SplitButton } from '../../SplitButton';
import { SVG } from '../../SVG';
import useContentResizeObserver from './useContentResizeObserver';
import useInteractionStopObserver from './useInteractionStopObserver';
import useZoomResizeObserver from './useZoomResizeObserver';
import cn from '../Dialog.css.json';
import '../Dialog.css';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHeaderId = createUseHtmlId('dialog-header');
const useAccentIconId = createUseHtmlId('dialog-accent-icon');
export const BaseDialogLayout = /*#__PURE__*/forwardRef((props, ref) => {
  const localize = useNCTextLocalizer('Dialog');
  const dt = useDesignTokens();
  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const isRTL = useIsRTL();
  const role = props.role ?? 'dialog';
  const dialogHeaderId = useHeaderId();
  const accentIconId = useAccentIconId();
  const outerConstraintsRef = useRef(null);
  const customFooterAlignment = props.customFooterAlignment ?? 'left';
  const draggableProp = props.draggable ?? true;
  const defaultDraggableConfig = {
    drag: true,
    dragListener: false,
    dragConstraints: outerConstraintsRef,
    dragElastic: false,
    dragMomentum: false
  };
  const draggableConfig = draggableProp === true ? defaultDraggableConfig : typeof draggableProp === 'object' ? {
    ...defaultDraggableConfig,
    ...draggableProp
  } : false;
  // Chcek if draggable is an object with keys.
  const draggable = typeof draggableConfig === 'object' && Object.keys(draggableConfig).length > 0 ? draggableConfig : undefined;
  const resizable = props.resizable ?? false;
  const dialogRef = useRef(null);
  const headerRef = useRef(null);
  const contentRef = useRef(null);
  const userHasInteracted = useRef(false);
  const convertedMaxWidth = convertCSSSizeToNumber(props.size?.maxWidth);
  const convertedMaxHeight = convertCSSSizeToNumber(props.size?.maxHeight);
  const maxHeight = useMotionValue(convertedMaxHeight && convertedMaxHeight < document.documentElement.clientHeight - 64 ? convertedMaxHeight : document.documentElement.clientHeight - 64);
  const maxWidth = useMotionValue(convertedMaxWidth && convertedMaxWidth < document.documentElement.clientWidth - 64 ? convertedMaxWidth : document.documentElement.clientWidth - 64);
  const ariaLabelledBy = props.accent ? `${accentIconId} ${dialogHeaderId}` : dialogHeaderId;
  const [size, setSize] = useState(props.size ?? {});
  const {
    viewportWidth: vw,
    viewportHeight: vh
  } = getViewportDimensions();
  const [enterAnimationStyle, setEnterAnimationStyle] = useState(props.hideAnimation ? '' : cn.animate);
  function getViewportDimensions() {
    return {
      viewportWidth: document.documentElement.clientWidth || 0,
      viewportHeight: document.documentElement.clientHeight || 0
    };
  }
  const tokens = {
    ...getDialogDesignTokens(dt, {
      accent: props.accent,
      variant: props.variant,
      contentPadding: props.contentPadding,
      footerControls: !!props.footerControls,
      renderCustomFooter: !!props.renderCustomFooter,
      isNewTokenValueFlagEnabled
    }),
    ...props.theme?.(dt)
  };
  let headerIcon;
  switch (props.accent) {
    case 'alert':
      headerIcon = ErrorStatus;
      break;
    case 'warning':
      headerIcon = WarningStatus;
      break;
    default:
      headerIcon = InformationStatus;
  }
  const title = props.title || props.ariaLabel;
  const ariaLabel = title ? localize('dialog.close.aria', {
    title
  }) : localize('dialog.close.default.aria');
  const closeIconButton = !props.hideCloseIcon && props.dismissAction ? /*#__PURE__*/_jsx("div", {
    className: cn.closeIconWrapper,
    children: /*#__PURE__*/_jsx(Button, {
      testId: postfixTestId(props.testId, 'closeIconBtn'),
      className: cn.closeIcon,
      variant: ButtonVariant.Icon,
      icon: {
        icon: Close,
        theme: () => {
          return {
            '--icon-default-override-fg': dt.themeVariant === 'light' ? 'var(--dialog-header-fg)' : undefined
          };
        }
      },
      ariaLabel: ariaLabel,
      onPress: props.dismissAction?.onPress
    })
  }) : null;

  // Make sure we only have one default button
  let defaultButton = undefined;
  if (props.firstAction?.isDefault) {
    defaultButton = 'first';
  } else if (props.secondAction?.isDefault) {
    defaultButton = 'second';
  } else if (props.dismissAction?.isDefault) {
    defaultButton = 'dismiss';
  }
  const handleKeyDown = e => {
    if (e.defaultPrevented) return;
    if (props.busyStateIndicatorProps?.active) {
      // No key should be handled if the current dialog is busy
      e.preventDefault();
      return;
    }
    userHasInteracted.current = true;
    if (!isEventMarked(e) && e.key === 'Escape') {
      // when dismissAction is not defined, Escape key should still be handled via propagation
      props.propagateKeyboardEvents ? e.preventDefault : e.stopPropagation();
      props.dismissAction?.onPress?.();
    }
    if (!isEventMarked(e) && e.key === 'Enter') {
      if (props.firstAction?.isDefault && !props.firstAction?.disabled) {
        e.preventDefault();
        props.firstAction.onPress?.();
      } else if (props.secondAction?.isDefault && !props.secondAction?.disabled) {
        e.preventDefault();
        props.secondAction.onPress?.();
      } else if (props.dismissAction?.isDefault && !props.dismissAction?.disabled) {
        e.preventDefault();
        props.dismissAction.onPress?.();
      }
    }
  };

  // Prevents the min-width from being set smaller than the width needed for the
  // footer buttons
  useEffect(() => {
    if (!dialogRef.current || !headerRef.current || !contentRef.current) return;
    const dialog = dialogRef.current;
    const header = headerRef.current;
    const content = contentRef.current;
    const currentMinWidth = dialog.style.minWidth;
    const currentMaxWidth = dialog.style.maxWidth;
    const currentWidth = dialog.style.width;

    // Get the initial min width in pixels since it could be set to rem/%/calc()
    dialog.style.width = '0';
    const currentMinWidthAsPx = dialog.offsetWidth;
    dialog.style.width = '';

    // Hide the dialog sections not related to the footer. remove the min/max widths of the
    // dialog so the footer can render at it's natural width.
    dialog.style.maxWidth = 'none';
    dialog.style.minWidth = '0';
    header.style.display = 'none';
    content.style.display = 'none';

    // Get the width of the dialog (which is only the footer)
    const offsetWidth = dialog.offsetWidth;

    // Reset the temporary styles
    dialog.style.width = currentWidth;
    dialog.style.maxWidth = currentMaxWidth;
    dialog.style.minWidth = currentMinWidth;
    header.style.display = '';
    content.style.display = '';

    // Update the minWidth if the footer is wider than the current minWidth
    let newMinWidth = currentMinWidthAsPx;
    if (offsetWidth > newMinWidth) {
      newMinWidth = offsetWidth;
      // Update the state
      setSize(prevSize => {
        return {
          ...prevSize,
          minWidth: newMinWidth
        };
      });
    }
  }, [size.minWidth]);
  function isInitialFocusConfigured() {
    return !!(props.firstAction?.initialFocus || props.secondAction?.initialFocus || props.dismissAction?.initialFocus);
  }
  const dragControls = useDragControls();
  function startDrag(event) {
    dragControls.start(event, {
      snapToCursor: false
    });
  }

  // Create a reference for the X and Y transform values
  const transformX = useMotionValue(0);
  const transformY = useMotionValue(0);

  // useAnimate allows us to implicitly trigger animations
  const [scope, animate] = useAnimate();

  // stores a ref to the current constraints
  const constraintsRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  // Determines how far away from the edge of the client window
  // the user may drag.
  const MOVE_OFFSET = 5;
  const onArrowKeyDrag = useCallback(e => {
    switch (e.key) {
      case 'ArrowLeft':
        {
          const x = transformX.get();
          const constraint = constraintsRef.current?.left ?? vw / 2;
          transformX.set(Math.max(x - MOVE_OFFSET, constraint));
          break;
        }
      case 'ArrowRight':
        {
          const x = transformX.get();
          const constraint = constraintsRef.current?.right ?? vw / 2;
          transformX.set(Math.min(x + MOVE_OFFSET, constraint));
          break;
        }
      case 'ArrowUp':
        {
          const y = transformY.get();
          const constraint = constraintsRef.current?.top ?? vh / 2;
          transformY.set(Math.max(y - MOVE_OFFSET, constraint));
          break;
        }
      case 'ArrowDown':
        {
          const y = transformY.get();
          const constraint = constraintsRef.current?.bottom ?? vh / 2;
          transformY.set(Math.min(y + MOVE_OFFSET, constraint));
          break;
        }
    }
  }, [transformX, transformY, vh, vw]);
  const dragIcon = draggable ? /*#__PURE__*/_jsx("div", {
    role: "img",
    tabIndex: 0,
    className: cn.dragIconWrapper,
    onKeyDown: e => {
      onArrowKeyDrag(e);
      const constraints = constraintsRef.current;
      const outerConstraints = outerConstraintsRef.current;
      const dialog = dialogRef.current;
      if (!constraints || !outerConstraints || !dialog) {
        return; // Exit early if any ref is missing
      }

      const newBottom = constraints.top + (outerConstraints.getBoundingClientRect().height - dialog.getBoundingClientRect().height);
      constraintsRef.current = {
        ...constraints,
        bottom: newBottom
      };
      calculateMaxHeightWidth(transformX.get(), transformY.get(), constraints);
    },
    "data-testid": postfixTestId(props.testId, 'dragIcon'),
    title: localize('dialog.dragIcon.label'),
    "aria-label": localize('dialog.dragIcon.aria'),
    children: /*#__PURE__*/_jsx(Icon, {
      theme: () => ({
        '--icon-default-override-fg': 'var(--dialog-header-fg)'
      }),
      className: cn.dragIcon,
      icon: GrabberReorderToken,
      tooltip: "",
      ariaLabel: ""
    })
  }) : null;
  const resizer = resizable ? /*#__PURE__*/_jsx(Resizer, {
    resizableElementRef: dialogRef,
    onResize: newSize => {
      const x = transformX.get();
      const y = transformY.get();
      const constraints = constraintsRef.current;

      // Resizer resizes the element in 5px increments with the keyboard.
      // If the current transform is within 5px of the boundary and attempting to get larger don't perform the resize.
      // Because we need to check if the size is shrinking or growing, in either direction, we handle height and width
      // separately. React will batch these 2 state updates.
      setSize(prevState => {
        // The resize is shrinking
        if (convertCSSSizeToNumber(prevState.height) >= newSize.height) {
          return {
            ...prevState,
            height: newSize.height
          };
        } else {
          // The resize is growing. Check if it's growing outside the boundary before updating state.
          if (constraints && Math.abs(y - constraints.bottom) >= 5) {
            return {
              ...prevState,
              height: newSize.height
            };
          }
        }
        // The resize was attempting to grow outside the boundary, so return the previous state.
        return prevState;
      });
      setSize(prevState => {
        // The resize is shrinking
        if (convertCSSSizeToNumber(prevState.width) >= newSize.width) {
          return {
            ...prevState,
            width: newSize.width
          };
        } else {
          // The resize is growing. Check if it's growing outside the boundary before updating state.
          if (constraints && Math.abs(x - (isRTL ? constraints.left : constraints.right)) >= 5) {
            return {
              ...prevState,
              width: newSize.width
            };
          }
        }
        // The resize was attempting to grow outside the boundary, so return the previous state.
        return prevState;
      });
    },
    setIsDragging: setIsDragging,
    testId: postfixTestId(props.testId, 'resizeGripIcon'),
    tooltip: localize('dialog.gripIcon.label'),
    ariaLabel: localize('dialog.gripIcon.aria')
  }) : null;
  const getMaxWidthAndMaxHeight = useCallback(() => {
    const {
      viewportWidth: vw,
      viewportHeight: vh
    } = getViewportDimensions();
    let constraintRect;
    let constraintHeight = vh;
    let constraintWidth = vw;
    if (constraintIsRef(draggable?.dragConstraints) && draggable?.dragConstraints?.current) {
      constraintRect = draggable?.dragConstraints?.current?.getBoundingClientRect();
      constraintHeight = constraintRect.height;
      constraintWidth = constraintRect.width;
    }

    // convertCSSSizeToNumber works only with few type of units.
    const assignedMaxWidth = convertCSSSizeToNumber(size.maxWidth);
    const assignedMaxHeight = convertCSSSizeToNumber(size.maxHeight);
    return {
      maxWidth: assignedMaxWidth > 0 ? Math.min(assignedMaxWidth, constraintWidth) : constraintWidth,
      maxHeight: assignedMaxHeight > 0 ? Math.min(assignedMaxHeight, constraintHeight) : constraintHeight
    };
  }, [size.maxWidth, size.maxHeight, draggable?.dragConstraints]);
  const calculateMaxHeightWidth = useCallback((x, y, constraints) => {
    const node = dialogRef.current;
    if (!node || !constraints) return;
    const offsetX = Math.abs(x - (isRTL ? constraints.left : constraints.right));
    const offsetY = Math.abs(y - constraints.bottom);
    const rect = node.getBoundingClientRect();

    // returns maxWidth/maxHeight based on outerConstraintRef and the size prop values if set
    const {
      maxWidth,
      maxHeight
    } = getMaxWidthAndMaxHeight();
    animate(scope.current, {
      maxHeight: Math.min(rect?.height + offsetY, maxHeight),
      maxWidth: Math.min(rect?.width + offsetX, maxWidth)
    }, {
      duration: 0
    });

    // The previously added explicit programmatic animations for X and Y positions for the conditions
    // rect.width > outerConstraintWidth oor rect.height > outerConstraintHeight was causing
    // flickering response and looked buggy. Hence removed it.
  }, [scope, animate, isRTL, getMaxWidthAndMaxHeight]);
  const centerDialog = useCallback(() => {
    const node = dialogRef.current;
    const outerNode = outerConstraintsRef.current;
    if (!node || !outerNode) return;
    const rect = node.getBoundingClientRect();
    const outerRect = outerNode.getBoundingClientRect();
    const dialogHeight = Math.min(rect.height, window.innerHeight - 64);
    const dialogWidth = Math.min(rect.width, window.innerWidth - 64);
    const offsetY = (outerRect.height - dialogHeight) / 2 - outerRect.height / 2;
    const desiredLeft = (outerRect.width - dialogWidth) / 2;
    const offsetX = isRTL ? -(desiredLeft - outerRect.width / 2) : desiredLeft - outerRect.width / 2;
    transformX.set(offsetX);
    transformY.set(offsetY);

    // Call your calculateMaxHeightWidth function
    calculateMaxHeightWidth(offsetX, offsetY, constraintsRef.current);
  }, [dialogRef, outerConstraintsRef, transformX, transformY, isRTL, calculateMaxHeightWidth]);
  const [ignoreContentObserver, setIgnoreContentObserver] = useState(false);
  useInteractionStopObserver(dialogRef, setIgnoreContentObserver);
  useContentResizeObserver(contentRef, ignoreContentObserver, centerDialog);
  useZoomResizeObserver(dialogRef, centerDialog);
  useEffect(() => {
    centerDialog(); // Initial centering
  }, [centerDialog]);

  // Remove animation class from the dialog after the loading animation completes
  useEffect(() => {
    dialogRef?.current?.addEventListener('animationend', () => {
      setEnterAnimationStyle('');
    });
  }, []);
  const variants = props.hideAnimation ? undefined : {
    hidden: {
      transformOrigin: 'center',
      scale: 0.8,
      opacity: 0,
      transition: {
        type: 'tween',
        duration: 0.3,
        ease: [0, 0.5, 0.2, 1]
      }
    }
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      ref: outerConstraintsRef,
      style: {
        position: 'fixed',
        inset: 32,
        zIndex: -1,
        pointerEvents: 'none'
      }
    }), /*#__PURE__*/_jsx(motion.div, {
      exit: "hidden",
      variants: variants,
      ...(draggable ? {
        ...draggable
      } : {}),
      dragControls: dragControls,
      onMeasureDragConstraints: measuredConstraints => {
        constraintsRef.current = measuredConstraints;
        const x = transformX.get();
        const y = transformY.get();
        if (!isDragging) {
          calculateMaxHeightWidth(x, y, measuredConstraints);
        }
      },
      onDragStart: () => {
        setIsDragging(true);
      },
      onDragEnd: () => {
        setIsDragging(false);
      },
      "data-testid": postfixTestId(props.testId, 'dialog'),
      ref: element => {
        dialogRef.current = element;
        setRef(ref, element);
        setRef(scope, element);
      },
      role: role,
      "aria-labelledby": props.ariaLabelledBy,
      ...(props.title && {
        'aria-labelledby': ariaLabelledBy
      }),
      className: cx(cn.dialog, enterAnimationStyle, props.subContentFooterControl && cn.withSubContentFooter, props.className),
      style: {
        ...tokens,
        ...props.style,
        x: transformX,
        y: transformY,
        ...size,
        maxWidth,
        maxHeight
      },
      "aria-describedby": props.ariaDescribedBy,
      "aria-label": props.ariaLabel,
      onKeyDown: handleKeyDown,
      onMouseDown: () => {
        userHasInteracted.current = true;
      },
      children: /*#__PURE__*/_jsx("div", {
        className: cn.dialogInner,
        role: props.isModal === false ? 'region' : undefined,
        "data-landmark-label": props.isModal === false ? props.landmarkLabel ?? props.title : undefined,
        style: {
          minHeight: size.minHeight
        },
        children: /*#__PURE__*/_jsxs(BusyStateIndicator, {
          ...props.busyStateIndicatorProps,
          fillParentContainer: true,
          containerClassName: cn.busyStateIndicatorContainer,
          children: [/*#__PURE__*/_jsxs("div", {
            ref: headerRef,
            "data-testid": postfixTestId(props.testId, 'header'),
            className: cx(cn.dialogHeader, draggable && cn.dragCursor, props.accent ? cn.accent : undefined),
            onPointerDown: startDrag,
            onMouseDown: e => {
              document.activeElement?.blur();
              e.preventDefault();
            },
            children: [props.headerImage ? /*#__PURE__*/_jsx(SVG, {
              className: cn.headerImage,
              icon: props.headerImage
            }) : null, props.headerLogo ? /*#__PURE__*/_jsx(SVG, {
              className: cn.headerLogo,
              icon: props.headerLogo
            }) : null, props.title && /*#__PURE__*/_jsx(Heading, {
              testId: postfixTestId(props.testId, 'heading'),
              id: dialogHeaderId,
              level: props.isModal ? 1 : 2,
              className: cx(cn.dialogTitle, cn.noDragCursor),
              variant: HeadingVariant.PrimaryLight,
              wrapText: props.titleWrapText,
              icon: props.accent ? {
                icon: headerIcon,
                className: cn.accentIcon
              } : undefined,
              children: props.title
            })]
          }), closeIconButton, /*#__PURE__*/_jsx("section", {
            ref: contentRef,
            className: cx(cn.dialogContent, !props.title && cn.noTitle),
            ...(!isInitialFocusConfigured() && {
              ['data-autofocus-inside']: true
            }),
            children: props.children
          }), props.subContentFooterControl ? /*#__PURE__*/_jsx("div", {
            className: cn.dialogSubFooter,
            "data-testid": postfixTestId(props.testId, 'subContentFooter'),
            ...(!isInitialFocusConfigured() && {
              ['data-autofocus-inside']: true
            }),
            children: props.subContentFooterControl
          }) : null, props.footerControls || props.firstAction || props.secondAction || props.dismissAction || props.renderCustomFooter ? /*#__PURE__*/_jsxs("div", {
            "data-testid": postfixTestId(props.testId, 'footer'),
            className: cn.dialogFooter,
            ...(defaultButton === undefined && !isInitialFocusConfigured() && {
              ['data-autofocus-inside']: true
            }),
            children: [props.renderCustomFooter && customFooterAlignment === 'left' && /*#__PURE__*/_jsx("div", {
              "data-autofocus-inside": true,
              "data-testid": postfixTestId(props.testId, 'footerControls'),
              children: props.renderCustomFooter?.()
            }), props.footerControls && /*#__PURE__*/_jsx("div", {
              className: cn.dialogFooterControls,
              "data-autofocus-inside": true,
              "data-testid": postfixTestId(props.testId, 'footerControls'),
              children: props.footerControls
            }), /*#__PURE__*/_jsxs(Flow, {
              spacing: "s",
              className: cn.dialogFooterButtons,
              children: [props.firstAction ? /*#__PURE__*/_jsx(DialogActionButton, {
                buttonRef: props.firstActionRef,
                action: props.firstAction,
                isDefault: defaultButton === 'first',
                testId: postfixTestId(props.testId, 'firstButton')
              }) : null, props.secondAction ? /*#__PURE__*/_jsx(DialogActionButton, {
                buttonRef: props.secondActionRef,
                action: props.secondAction,
                isDefault: defaultButton === 'second',
                testId: postfixTestId(props.testId, 'secondButton')
              }) : null, props.dismissAction ? /*#__PURE__*/_jsx(DialogActionButton, {
                buttonRef: props.dismissActionRef,
                action: props.dismissAction,
                isDefault: defaultButton === 'dismiss',
                testId: postfixTestId(props.testId, 'dismissButton')
              }) : null]
            }), props.renderCustomFooter && customFooterAlignment === 'right' && /*#__PURE__*/_jsx("div", {
              "data-autofocus-inside": true,
              "data-testid": postfixTestId(props.testId, 'footerControls'),
              children: props.renderCustomFooter?.()
            })]
          }) : null, resizer, dragIcon]
        })
      })
    })]
  });
});
export const getDialogDesignTokens = (dt, opts) => {
  let accentColor;
  if (opts?.accent) {
    switch (opts?.accent) {
      case 'alert':
        accentColor = {
          accent: dt.tokens['color-status-alert'],
          bg: 'transparent',
          'drag-icon-focus': 'var(--focus-light-bg)'
        };
        break;
      case 'warning':
        accentColor = {
          accent: dt.tokens['color-status-warning'],
          bg: 'transparent',
          'drag-icon-focus': 'var(--focus-light-bg)'
        };
        break;
      default:
        accentColor = {
          accent: dt.tokens['color-status-info'],
          bg: 'transparent',
          'drag-icon-focus': 'var(--focus-light-bg)'
        };
    }
  }
  // Dialog Padding - content section
  // If contentPadding is provided as a string for the CSS padding shorthand (2, 3, or 4 values), we need to isolate the left padding
  // so it can be applied to the footer for content alignment.

  let startPadding = opts?.contentPadding;
  if (opts?.contentPadding && typeof opts?.contentPadding === 'string' && opts?.contentPadding.includes(' ')) {
    const paddingValues = opts?.contentPadding.split(' ');
    if (paddingValues.length < 4) {
      startPadding = paddingValues[1];
    } else {
      startPadding = paddingValues[3];
    }
  }
  const paddingTokens = {
    pd: opts?.contentPadding ?? dt.tokens['space-base-xl'],
    'no-title-start-pd': dt.tokens['space-base-null'],
    'start-pd': startPadding
  };
  return {
    ...renderDesignTokens({
      ...paddingTokens,
      fg: dt.tokens['color-text-neutral'],
      bg: dt.tokens['color-container-background-0'],
      bd: dt.tokens['color-container-border-4'],
      bw: dt.tokens['size-border-width-s'],
      sh: dt.tokens['shadow-depth-xl'],
      'border-radius': dt.tokens['size-border-radius-xs']
    }, 'dialog-content'),
    ...renderDesignTokens({
      fg: dt.tokens['color-heading-text'],
      'font-size': dt.tokens['font-size-heading-text-3'],
      'font-weight': dt.tokens['font-style-weight-700'],
      'space-top': dt.tokens['space-base-xl'],
      'space-bottom': dt.tokens['space-base-null'],
      'icon-size': dt.tokens['size-icon-m'],
      bg: 'transparent',
      'drag-icon-focus': 'var(--focus-light-bg)',
      'drag-icon-focus-outline': 'calc(-1 * var(--focus-width))',
      'drag-icon-inset': '3px',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'drag-icon-inset': 0,
        'drag-icon-focus-outline': 'calc(-1 * var(--focus-width) - 2px)'
      }),
      ...accentColor
    }, 'dialog-header'),
    ...renderDesignTokens({
      s: dt.tokens['space-base-s'],
      m: dt.tokens['space-base-m'],
      l: dt.tokens['space-base-l'],
      xl: dt.tokens['space-base-xl'],
      none: dt.tokens['space-base-null']
    }, 'dialog-spacing'),
    ...renderDesignTokens({
      'anim-dur-enter': dt.tokens['duration-300'],
      'anim-tf-enter': dt.tokens['ease-out'],
      'anim-dur-exit': dt.tokens['duration-250'],
      'anim-tf-exit': dt.tokens['ease-out'],
      fg: dt.tokens['color-text-neutral-inverse'],
      'scale-enter': '1.00001'
    }, 'dialog'),
    ...renderDesignTokens({
      width: opts?.footerControls ? 'auto' : '100%',
      stretchToFit: opts?.footerControls ? 'inherit' : '1'
    }, 'dialog-footer'),
    ...(!dt.enableNovaTokens && getDialogLegacyDesignTokens(dt, opts))
  };
};
export const getDialogLegacyDesignTokens = (dt, opts) => {
  // Dialog Header section
  let reportThemeName = {};
  let themeVariant = {};
  if (!opts?.accent) {
    switch (dt.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        reportThemeName = {
          bg: dt.c.black,
          fg: dt.c.white
        };
    }
    switch (dt.themeVariant) {
      case 'dark':
        themeVariant = {
          bg: 'transparent',
          fg: dt.c.white
        };
        break;
      case 'high-contrast':
        themeVariant = {
          bg: 'transparent',
          fg: dt.c.white
        };
        break;
      default:
        themeVariant = {
          bg: 'transparent',
          fg: dt.c.n_3
        };
    }
  }
  let accentColor;
  if (opts?.accent) {
    switch (opts?.accent) {
      case 'alert':
        accentColor = {
          accent: dt.c.alert,
          bg: 'transparent',
          fg: dt.c.alert
        };
        break;
      case 'warning':
        accentColor = {
          accent: dt.c.warning,
          bg: 'transparent',
          fg: dt.c.warning
        };
        break;
      default:
        accentColor = {
          accent: dt.c.informational,
          bg: 'transparent',
          fg: dt.c.informational
        };
    }
  }
  return {
    ...renderDesignTokens({
      ...dt.reportTokens.backgroundsDepth.large
    }, 'dialog-content'),
    ...renderDesignTokens({
      'font-size-m': dt.reportTokens.font.size.m,
      'font-weight': dt.reportTokens.font.weight.thick
    }, 'dialog'),
    ...renderDesignTokens({
      ...themeVariant,
      ...reportThemeName,
      ...accentColor
    }, 'dialog-header')
  };
};
const isDialogSplitAction = action => {
  return action.items !== undefined;
};
const DialogActionButton = props => {
  const action = props.action;
  const buttonRef = useRef(null);
  return isDialogSplitAction(action) ? /*#__PURE__*/_jsx(SplitButton, {
    ref: element => {
      buttonRef.current = element;
      setRef(props.buttonRef, element);
    },
    testId: props.testId,
    variant: props.isDefault ? ButtonVariant.Primary : ButtonVariant.Standard,
    text: action.text,
    onPress: action.onPress,
    autoFocus: action.isDefault || action.initialFocus ? true : undefined,
    disabled: action.disabled,
    items: action.items,
    updateButtonAction: action.updateButtonAction,
    onItemSelected: action.onItemSelected
  }) : /*#__PURE__*/_jsx(Button, {
    ref: element => {
      buttonRef.current = element;
      setRef(props.buttonRef, element);
    },
    testId: props.testId,
    variant: props.isDefault ? ButtonVariant.Primary : ButtonVariant.Standard,
    onPress: action.onPress,
    "data-autofocus": action.isDefault || action.initialFocus ? true : undefined,
    disabled: action.disabled,
    text: action.text
    // TODO: Per Accessibility remark we have to remove ariaLabel for text buttons.
    // We need a separate bug for it as it may be a breaking change (from tests perspective).
    // this prop is currently marked as deprecated in DialogProps.
    ,
    ariaLabel: action.ariaLabel,
    className: action.className
  });
};
const constraintIsRef = constraint => {
  return typeof constraint === 'object' && constraint.hasOwnProperty('current');
};
//# sourceMappingURL=BaseDialogLayout.js.map